// JMUI TODO fix all rules-of-hooks violations (not specific to this port)
import type { ViewType } from '../../../../constants/ViewType';
import type { QualificationAttribute } from '../../../../types/JobAttribute';
import type { InlineQuestionProps } from '../../../common/InlineQuestion';
import type { TileSectionProps, TileSectionSentimentCounts } from '../../../common/TileSection';

import {
    filterByCategory,
    getAllMissingQualifications,
    sortRequirementsByTrafficLights,
} from '../';
import { useI18n } from '../../../../../../../../../shared/I18n';
import Sentiment from '../../../../../../../../../shared/util/salary/Sentiment';
import { useLogging } from '../../../../context/useLogging';
import { InferenceMethod } from '../../../../requirements/TaxonomyEntity';
import InlineQuestionResponse from '../../../../types/InlineQuestionResponse';
import YellowLightCategoryType from '../../../../types/YellowLightCategoryType';
import { useSectionErrors } from '../../../../utils/useSectionErrors';
import { useRemoveJobSeekerProfileStructuredData } from '../../graphql/useRemoveJobSeekerProfileStructuredData';
import { useSendQualificationResponse } from '../../graphql/useSendQualificationResponse';
import { getInlineQuestion, useQualificationSection } from '../useQualificationSection';

import { useCallback, useEffect, useMemo, useState } from 'react';

export const VALID_YELLOWLIGHT_CATEGORIES = [
    YellowLightCategoryType.LICENSE,
    YellowLightCategoryType.CERTIFICATIONS,
    YellowLightCategoryType.SKILLS,
    YellowLightCategoryType.EDUCATION,
    YellowLightCategoryType.LANGUAGES,
];

export const getYellowLightCategories = (
    attributes: QualificationAttribute[],
): YellowLightCategoryType[] => {
    return VALID_YELLOWLIGHT_CATEGORIES.filter(
        (category) => filterByCategory(attributes, category).length > 0,
    );
};

export function getSegmentIdsFromOccupations(occupations: {
    [key: string]: Array<{ uuid: string; suid: string }>;
}): Set<string> {
    const segmentClassIds = new Set<string>();
    Object.values(occupations).forEach((occupation) => {
        occupation.forEach((segClass) => {
            segmentClassIds.add(segClass.uuid);
            segmentClassIds.add(segClass.suid);
        });
    });
    return segmentClassIds;
}

export const getQualificationTrackingId = (qualification: QualificationAttribute) => {
    return qualification.jssdId || qualification.uuid || qualification.suid;
};
export const getQualificationResponse = (sentiment: Sentiment) => {
    const qualificationResponse: InlineQuestionResponse =
        sentiment === Sentiment.POSITIVE ? InlineQuestionResponse.YES : InlineQuestionResponse.NO;
    return qualificationResponse;
};

export const aggregateSentimentCounts = (
    sections: TileSectionProps[],
): TileSectionSentimentCounts => {
    const aggregatedSentimentCounts = {
        total: 0,
        positive: 0,
        negative: 0,
        unknown: 0,
    };

    sections
        .map((section) => section.sentimentCounts)
        .forEach((sentimentCounts) => {
            aggregatedSentimentCounts.total += sentimentCounts?.total ?? 0;
            aggregatedSentimentCounts.positive += sentimentCounts?.positive ?? 0;
            aggregatedSentimentCounts.negative += sentimentCounts?.negative ?? 0;
            aggregatedSentimentCounts.unknown += sentimentCounts?.unknown ?? 0;
        });

    return aggregatedSentimentCounts;
};

const getAllInlineQuestions = (
    attributes: QualificationAttribute[],
    answeredIds: string[],
    onInlineSubmit: (p: {
        response: InlineQuestionResponse;
        attribute: QualificationAttribute;
    }) => Promise<boolean>,
    viewType: ViewType,
): Array<InlineQuestionProps> => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const gt = useI18n();

    const reqs: QualificationAttribute[] = sortRequirementsByTrafficLights(attributes, viewType);

    const missingQualifications = getAllMissingQualifications(reqs, answeredIds);
     
    // eslint-disable-next-line no-array-constructor -- RN-1640
    const results: Array<InlineQuestionProps> = new Array<InlineQuestionProps>();

    missingQualifications.map((qualification) =>
        qualification
            ? results.push({
                  label: getInlineQuestion(qualification, gt),
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
                  onSubmit: async (response: InlineQuestionResponse) => {
                      await onInlineSubmit({
                          response,
                          attribute: qualification,
                      });
                  },
              })
            : {},
    );

    return results;
};

/**
 * Creates a stable key from the initial attributes to detect when we're viewing a different job.
 * This allows us to reset local state when the underlying data source changes.
 */
const getAttributesStableKey = (attributes: QualificationAttribute[]): string => {
    return attributes.map((attr) => attr.suid).join(',');
};

export const useQualificationSections = ({
    attributes: initialAttributes,
    viewType,
}: {
    attributes: QualificationAttribute[];
    viewType: ViewType;
}) => {
    const logger = useLogging();
    const { onAddError, errors, onClearError } = useSectionErrors();
    const sendQualificationResponse = useSendQualificationResponse();
    const removeJobSeekerProfileStructuredData = useRemoveJobSeekerProfileStructuredData();

    // Create a stable key to detect when we're viewing a different job
    const attributesKey = useMemo(
        () => getAttributesStableKey(initialAttributes),
        [initialAttributes],
    );

    // Store attributes in local state to persist changes across re-renders.
    // This fixes the bug where scrolling caused attribute changes to be lost
    // because the parent component was creating fresh attribute objects on each render.
    const [localAttributes, setLocalAttributes] = useState<QualificationAttribute[]>(() =>
        initialAttributes.map((attr) => ({ ...attr })),
    );
    const [answeredIds, setAnsweredIds] = useState<string[]>([]);
    const [lastAttributesKey, setLastAttributesKey] = useState(attributesKey);

    // Reset local state when the underlying attributes change (e.g., navigating to a different job)
    useEffect(() => {
        if (attributesKey !== lastAttributesKey) {
            setLocalAttributes(initialAttributes.map((attr) => ({ ...attr })));
            setAnsweredIds([]);
            setLastAttributesKey(attributesKey);
        }
    }, [attributesKey, lastAttributesKey, initialAttributes]);

    // Helper to update a specific attribute in local state
    const updateAttribute = useCallback(
        (
            targetAttribute: QualificationAttribute,
            updates: Partial<
                Pick<QualificationAttribute, 'sentiment' | 'jssdId' | 'inferenceMethod'>
            >,
        ) => {
            setLocalAttributes((prev) =>
                prev.map((attr) => {
                    if (attr.suid === targetAttribute.suid) {
                        return { ...attr, ...updates };
                    }
                    return attr;
                }),
            );
        },
        [],
    );

    // Find the local attribute that corresponds to the passed attribute reference
    const getLocalAttribute = useCallback(
        (attribute: QualificationAttribute): QualificationAttribute => {
            return localAttributes.find((attr) => attr.suid === attribute.suid) ?? attribute;
        },
        [localAttributes],
    );

    const onQualificationSubmissionsSuccess = useCallback(
        (
            qualification: QualificationAttribute,
            submissionSentiment: InlineQuestionResponse,
            jssdId?: string,
        ) => {
            let newSentiment: Sentiment | undefined;
            switch (submissionSentiment) {
                case InlineQuestionResponse.YES:
                    newSentiment = Sentiment.POSITIVE;
                    break;
                case InlineQuestionResponse.NO:
                    newSentiment = Sentiment.NEGATIVE;
                    break;
                default:
                    break;
            }

            // Update local state immutably instead of mutating the prop
            if (newSentiment !== undefined) {
                updateAttribute(qualification, {
                    sentiment: newSentiment,
                    inferenceMethod: InferenceMethod.DIRECT,
                    ...(jssdId !== undefined && { jssdId }),
                });
            }

            setAnsweredIds((prev) => [...prev, getQualificationTrackingId(qualification)]);
        },
        [updateAttribute],
    );

    const onSendResponse = useCallback(
        async ({
            attribute,
            response,
        }: {
            attribute: QualificationAttribute;
            response: InlineQuestionResponse;
        }) => {
            return sendQualificationResponse({
                attribute,
                questionResponse: response,
                onQualificationSubmissionsSuccess,
                onQualificationSubmissionsFailure: onAddError,
            });
        },
        [sendQualificationResponse, onQualificationSubmissionsSuccess, onAddError],
    );

    const inlineSubmitCallback = useCallback(
        async ({
            response,
            attribute,
        }: {
            response: InlineQuestionResponse;
            attribute: QualificationAttribute;
        }): Promise<boolean> => {
            // Get the local attribute to read the current inferenceMethod
            const localAttr = getLocalAttribute(attribute);

            if (response === InlineQuestionResponse.SKIP) {
                logger.logQualificationsQuestionsSkip(localAttr);
                onQualificationSubmissionsSuccess(localAttr, InlineQuestionResponse.SKIP);
                return true;
            } else {
                const originalInferenceMethod = localAttr.inferenceMethod;
                const result = await onSendResponse({
                    response,
                    attribute: localAttr,
                });
                logger.logQualificationsQuestionButtonClick(
                    response,
                    localAttr,
                    result,
                    originalInferenceMethod!,
                );
                return result;
            }
        },
        [logger, onQualificationSubmissionsSuccess, onSendResponse, getLocalAttribute],
    );

    const yellowLightCategories = getYellowLightCategories(localAttributes);

    const onModalResetCallback = useCallback(
        async (attribute: QualificationAttribute) => {
            // Get the local attribute to read the current jssdId
            const localAttr = getLocalAttribute(attribute);
            let resetSucceed = false;
            try {
                await removeJobSeekerProfileStructuredData(localAttr, [localAttr.jssdId]);

                // Update local state immutably instead of mutating the prop
                updateAttribute(localAttr, { sentiment: Sentiment.UNKNOWN });

                setAnsweredIds((prev) =>
                    prev.filter(
                        (id) => ![localAttr.jssdId, localAttr.uuid, localAttr.suid].includes(id),
                    ),
                );

                resetSucceed = true;
            } catch (err) {
                onAddError(
                    err instanceof Error ? { message: err.message } : { message: String(err) },
                );
            }

            logger.logQualificationsQuestionsReset(localAttr, resetSucceed);

            if (!resetSucceed) {
                return false;
            }

            return true;
        },
        [
            removeJobSeekerProfileStructuredData,
            updateAttribute,
            onAddError,
            logger,
            getLocalAttribute,
        ],
    );

    const onModalSubmitCallback = useCallback(
        async ({
            attribute,
            sentiment,
        }: {
            attribute: QualificationAttribute;
            sentiment: Sentiment;
        }) => {
            const response = getQualificationResponse(sentiment);
            // Get the local attribute to ensure we're working with the current state
            const localAttr = getLocalAttribute(attribute);

            return onSendResponse({
                response,
                attribute: localAttr,
            });
        },
        [onSendResponse, getLocalAttribute],
    );

    const sections = yellowLightCategories.map((category: YellowLightCategoryType) =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useQualificationSection({
            attributes: localAttributes,
            category,
            answeredIds,
            onModalReset: onModalResetCallback,
            onModalSubmit: onModalSubmitCallback,
            onInlineSubmit: inlineSubmitCallback,
            viewType,
        }),
    );

    const sentimentCounts = aggregateSentimentCounts(sections);

    const missingQualifications: Array<InlineQuestionProps> = getAllInlineQuestions(
        localAttributes,
        answeredIds,
        inlineSubmitCallback,
        viewType,
    );

    return {
        sections,
        errors,
        onClearError,
        answeredIds,
        sentimentCounts,
        missingQualifications,
        inlineSubmitCallback,
        onModalResetCallback,
        localAttributes,
    };
};
